import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useProfiles } from '../../hooks/useProfiles';
import { useMilestoneWatchlist } from '../../hooks/useMilestones';
import { isDelayedRow } from '../../utils/milestonesFilter';
import {
    PageHeader,
    MetricCard,
    StatusBadge,
    EmptyState,
    SkeletonMetricCard,
    SkeletonChart,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import {
    FiUsers,
    FiTrendingUp,
    FiAlertTriangle,
    FiActivity,
    FiChevronRight,
    FiCheckCircle,
} from 'react-icons/fi';
import { GROWTH_STATUS } from '../../utils/growthStatus';
import './Dashboard.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
);

const Dashboard = () => {
    const navigate = useNavigate();
    const { data: childrenData, loading, error } = useProfiles();
    const { data: milestoneWatchlist } = useMilestoneWatchlist();
    // Window for the Registration Trends chart. Default 12 months;
    // toggled via the segmented control in the panel header.
    const [trendMonths, setTrendMonths] = useState(6);

    if (loading) {
        return (
            <div className="dashboard-wrapper">
                <div className="dashboard-grid">
                    <div className="dashboard-metrics-grid">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonMetricCard key={i} />
                        ))}
                    </div>
                    <div className="dashboard-bottom-grid">
                        <SkeletonChart height={280} />
                        <div className="dashboard-action-stack">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="dashboard-action-placeholder" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load dashboard"
                    description={error}
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => window.location.reload()}
                        >
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    const safeData = childrenData || [];
    const totalRegistrations = safeData.length;

    const vaccStats = { upToDate: 0, upcoming: 0, due: 0, overdue: 0 };
    const growthStats = {
        normal: 0,
        underweight: 0,
        severelyUnderweight: 0,
        overweight: 0,
        obese: 0,
        wasting: 0,
        severeWasting: 0,
        stunting: 0,
        severeStunting: 0,
    };

    // Severity-ordered growth buckets. Per-bucket counting — a child
    // flagged with both "Severe Wasting" AND "Underweight" counts in
    // BOTH severeWasting and underweight. First-match-wins was the
    // previous bug (the underweight card showed 0 when the same child
    // also had severe wasting). Same pattern as GrowthMonitoringReport
    // and VaccineSummaries: bucket totals reflect "how many children
    // carry this indicator", not "how many have this as their most-
    // severe indicator". The Immediate Action pane reads these as
    // tiles, so a child landing in multiple buckets makes the totals
    // exceed totalRegistrations — the same trade-off the user
    // accepted on the reports.
    const GROWTH_BUCKETS = [
        ['severeWasting', GROWTH_STATUS.SEVERE_WASTING],
        ['wasting', GROWTH_STATUS.WASTING],
        ['severeStunting', GROWTH_STATUS.SEVERE_STUNTING],
        ['stunting', GROWTH_STATUS.STUNTING],
        ['severelyUnderweight', GROWTH_STATUS.SEVERELY_UNDERWEIGHT],
        ['underweight', GROWTH_STATUS.UNDERWEIGHT],
        ['obese', GROWTH_STATUS.OBESE],
        ['overweight', GROWTH_STATUS.OVERWEIGHT],
        ['normal', GROWTH_STATUS.NORMAL],
    ];

    safeData.forEach((child) => {
        // Each child lands in "Up to Date" if they have NO pending doses
        // (matches the BE filter at profile_service.py — "no overdue, due,
        // or upcoming"). Otherwise they bump every pending bucket they
        // carry a dose in. Sum can still exceed safeData.length for
        // growth, but for vaccination the four buckets are mutually
        // exclusive (a child is Up to Date OR has at least one pending
        // dose, never both) so the click-through count matches the tile.
        const counts = child.vaccinationCounts || {};
        const pending = (counts.overdue || 0) + (counts.due || 0) + (counts.upcoming || 0);
        if (pending === 0) {
            vaccStats.upToDate += 1;
        } else {
            if ((counts.upcoming || 0) > 0) vaccStats.upcoming += 1;
            if ((counts.due || 0) > 0) vaccStats.due += 1;
            if ((counts.overdue || 0) > 0) vaccStats.overdue += 1;
        }

        // growthStatuses is a list (new API). Count the child in EVERY
        // bucket they carry a status for — e.g. [Underweight, Stunting]
        // bumps BOTH underweight and stunting. Children with no growth
        // records (empty growthStatuses) are NOT counted in Normal — the
        // BE filter ?growth=Normal is strict ("statuses contains Normal")
        // and the dashboard tile must match the click-through exactly.
        const statuses = Array.isArray(child.growthStatuses)
            ? child.growthStatuses
            : [];
        GROWTH_BUCKETS.forEach(([key, status]) => {
            if (statuses.includes(status)) {
                growthStats[key] += 1;
            }
        });
    });

    // Milestones: delayed = unique children in the Reminders → Milestones
    // triage queue (CDSS watchlist filtered to DELAYED rows only — PENDING
    // and DUE are off that surface). on-track = total registered minus
    // delayed. Rows can repeat per (child, milestone), so the Set
    // dedupes by childId. Uses the same isDelayedRow() the Reminders
    // page uses, so the two counts always agree.
    // Inline (no useMemo) because the Set build is cheap and we want to
    // keep all hooks above any early return.
    const { delayedChildCount, milestonesOnTrack } = (() => {
        const delayed = (milestoneWatchlist?.items || []).filter(isDelayedRow);
        const ids = new Set(delayed.map((r) => r.childId));
        return {
            delayedChildCount: ids.size,
            milestonesOnTrack: Math.max(0, totalRegistrations - ids.size),
        };
    })();

    const getRegistrationTrends = (monthCount = 12) => {
        const monthCounts = {};
        const monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
        ];
        const today = new Date();
        const labels = [];
        // Rolling N months ending at the current month. JS Date normalizes
        // out-of-range months (e.g. month -2 → Oct of the previous year), so
        // a single loop handles year crossover cleanly.
        for (let i = monthCount - 1; i >= 0; i -= 1) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const labelStr = `${monthNames[d.getMonth()]} '${d
                .getFullYear()
                .toString()
                .slice(2)}`;
            monthCounts[labelStr] = 0;
            labels.push(labelStr);
        }
        safeData.forEach((child) => {
            if (child.registeredDate) {
                const date = new Date(child.registeredDate);
                const labelStr = `${monthNames[date.getMonth()]} '${date
                    .getFullYear()
                    .toString()
                    .slice(2)}`;
                if (monthCounts[labelStr] !== undefined) {
                    monthCounts[labelStr] += 1;
                }
            }
        });
        return { labels, data: labels.map((label) => monthCounts[label]) };
    };

    const trendData = getRegistrationTrends(trendMonths);

    const chartData = {
        labels: trendData.labels,
        datasets: [
            {
                label: 'New Registrations',
                data: trendData.data,
                backgroundColor: 'rgba(14, 165, 233, 0.85)',
                hoverBackgroundColor: 'rgba(2, 132, 199, 0.95)',
                borderRadius: 6,
                maxBarThickness: 36,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#0f172a',
                titleFont: { size: 13, family: "'Inter', sans-serif", weight: 600 },
                bodyFont: { size: 12, family: "'Inter', sans-serif" },
                padding: 12,
                cornerRadius: 8,
                displayColors: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    font: { family: "'Inter', sans-serif" },
                    color: '#64748b',
                },
                grid: { color: 'rgba(148, 163, 184, 0.18)' },
                border: { display: false },
            },
            x: {
                ticks: { font: { family: "'Inter', sans-serif" }, color: '#64748b' },
                grid: { display: false },
                border: { display: true, color: '#cbd5e1' },
            },
        },
    };

    if (totalRegistrations === 0) {
        return (
            <div className="dashboard-wrapper">
                <EmptyState
                    illustration="no-data"
                    title="No registered children yet"
                    description="Start by registering your first child to see clinic insights here."
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => navigate('/add-child')}
                        >
                            Register a child
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="dashboard-wrapper">
            <PageHeader
                title="Clinic Dashboard"
                icon={FiActivity}
            />

            <div className="dashboard-grid">
                <div className="dashboard-metrics-grid">
                    <MetricCard
                        label="Total Registrations"
                        value={totalRegistrations}
                        accent="primary"
                        icon={FiUsers}
                        onClick={() => navigate('/profiles')}
                    />

                    <div className="metric-card dashboard-multi-card">
                        <span
                            className="metric-card__accent"
                            style={{ background: 'var(--pt-success)' }}
                            aria-hidden="true"
                        />
                        <div className="dashboard-multi-card__title">
                            <FiActivity size={16} /> Vaccination Status
                        </div>
                        <div className="dashboard-multi-card__rows">
                            <button
                                type="button"
                                className="dashboard-multi-card__row--success"
                                onClick={() => navigate('/profiles', { state: { filter: 'Up to Date' } })}
                            >
                                <StatusBadge severity="success" dot>Up to Date</StatusBadge>
                                <strong>{vaccStats.upToDate}</strong>
                            </button>
                            <button
                                type="button"
                                className="dashboard-multi-card__row--info"
                                onClick={() => navigate('/profiles', { state: { filter: 'Upcoming' } })}
                            >
                                <StatusBadge severity="info" dot>Upcoming</StatusBadge>
                                <strong>{vaccStats.upcoming}</strong>
                            </button>
                            <button
                                type="button"
                                className="dashboard-multi-card__row--warning"
                                onClick={() => navigate('/profiles', { state: { filter: 'Due' } })}
                            >
                                <StatusBadge severity="warning" dot>Due</StatusBadge>
                                <strong>{vaccStats.due}</strong>
                            </button>
                        </div>
                    </div>

                    <div className="dashboard-metrics-stack">
                        <div className="metric-card dashboard-multi-card">
                            <span
                                className="metric-card__accent"
                                style={{ background: 'var(--pt-primary)' }}
                                aria-hidden="true"
                            />
                            <div className="dashboard-multi-card__title">
                                <FiTrendingUp size={16} /> Growth Status
                            </div>
                            <div className="dashboard-multi-card__rows">
                                <button
                                    type="button"
                                    className="dashboard-multi-card__row--success"
                                    onClick={() => navigate('/profiles', { state: { filter: 'Normal' } })}
                                >
                                    <StatusBadge severity="success" dot>Normal</StatusBadge>
                                    <strong>{growthStats.normal}</strong>
                                </button>
                            </div>
                        </div>

                        <div className="metric-card dashboard-multi-card">
                            <span
                                className="metric-card__accent"
                                style={{ background: 'var(--pt-error)' }}
                                aria-hidden="true"
                            />
                            <div className="dashboard-multi-card__title">
                                <FiCheckCircle size={16} /> Milestones Status
                            </div>
                            <div className="dashboard-multi-card__rows">
                                <button
                                    type="button"
                                    className={`dashboard-multi-card__row--${delayedChildCount > 0 ? 'danger' : 'success'}`}
                                    onClick={() => navigate('/reminders?tab=milestones')}
                                >
                                    <StatusBadge severity={delayedChildCount > 0 ? 'danger' : 'success'} dot>
                                        {delayedChildCount > 0 ? 'Delayed' : 'None delayed'}
                                    </StatusBadge>
                                    <strong>{delayedChildCount}</strong>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-bottom-grid">
                    <div className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <h3>Registration Trends</h3>
                            <div
                                className="dashboard-range-selector"
                                role="group"
                                aria-label="Trend range"
                            >
                                {[3, 6, 12].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        className={`dashboard-range-selector__btn${trendMonths === m ? ' is-active' : ''}`}
                                        onClick={() => setTrendMonths(m)}
                                        aria-pressed={trendMonths === m}
                                    >
                                        {m} Months
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="dashboard-chart-body">
                            <Bar data={chartData} options={chartOptions} />
                        </div>
                    </div>

                    <div className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <h3>
                                <FiAlertTriangle size={16} /> Immediate Action
                            </h3>
                        </div>
                        <div className="dashboard-action-stack">
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${vaccStats.overdue > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Overdue' } })}
                            >
                                <span>Overdue Vaccinations</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{vaccStats.overdue}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.severeWasting > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Severe Wasting' } })}
                            >
                                <span>Severe Wasting</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.severeWasting}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.severeStunting > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Severe Stunting' } })}
                            >
                                <span>Severe Stunting</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.severeStunting}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.severelyUnderweight > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Severely Underweight' } })}
                            >
                                <span>Severely Underweight</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.severelyUnderweight}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.wasting > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Wasting' } })}
                            >
                                <span>Wasting</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.wasting}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.stunting > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Stunting' } })}
                            >
                                <span>Stunting</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.stunting}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--danger${growthStats.underweight > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Underweight' } })}
                            >
                                <span>Underweight</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.underweight}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--warning${growthStats.overweight > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Overweight' } })}
                            >
                                <span>Overweight</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.overweight}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`dashboard-action dashboard-action--warning${growthStats.obese > 0 ? '' : ' is-zero'}`}
                                onClick={() => navigate('/profiles', { state: { filter: 'Obese' } })}
                            >
                                <span>Obese</span>
                                <span className="dashboard-action__trailing">
                                    <strong>{growthStats.obese}</strong>
                                    <FiChevronRight size={16} aria-hidden="true" />
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
